import { z } from 'zod';
import { MANGEL_KATEGORIEN_STANDARD, ZAEHLER_ARTEN_STANDARD } from './form-schema.js';

/**
 * Wertformen der Spezialfelder. Sie liegen so in `Protocol.data` und gehen
 * damit in den `dataHash` ein — jede Änderung hier ändert bestehende Hashes
 * und ist deshalb nur mit neuer Template-Version zulässig.
 */

/**
 * Referenz auf ein Foto. Die `id` ist zugleich die `Document.id`; der S3-Key
 * wird erst beim Rendern aufgelöst. So bleibt `data` unabhängig davon, wann
 * der Upload fertig wurde.
 */
export const fotoReferenzSchema = z.object({
  id: z.string().min(1),
  beschriftung: z.string().max(300).optional(),
  aufgenommenAm: z.string().optional(),
  breite: z.number().int().positive().optional(),
  hoehe: z.number().int().positive().optional(),
});
export type FotoReferenz = z.infer<typeof fotoReferenzSchema>;

export const MANGEL_SCHWEREGRADE = ['gering', 'mittel', 'erheblich'] as const;
export type MangelSchweregrad = (typeof MANGEL_SCHWEREGRADE)[number];

export const MANGEL_STATUS = ['OFFEN', 'IN_ARBEIT', 'BEHOBEN', 'ABGENOMMEN', 'ABGELEHNT'] as const;
export type MangelStatus = (typeof MANGEL_STATUS)[number];

export const MANGEL_STATUS_LABEL: Record<MangelStatus, string> = {
  OFFEN: 'Offen',
  IN_ARBEIT: 'In Arbeit',
  BEHOBEN: 'Behoben',
  ABGENOMMEN: 'Abgenommen',
  ABGELEHNT: 'Abgelehnt',
};

export const mangelSchema = z.object({
  id: z.string().min(1),
  kategorie: z.string().max(100).optional(),
  beschreibung: z.string().min(1, 'Ein Mangel braucht eine Beschreibung').max(2000),
  schweregrad: z.enum(MANGEL_SCHWEREGRADE).optional(),
  /** Verursacher/Zuordnung, z. B. „Mieter", „Vermieter", „unklar". */
  zuordnung: z.string().max(100).optional(),
  kostenschaetzung: z.number().min(0).optional(),
  fotos: z.array(fotoReferenzSchema).default([]),
  status: z.enum(MANGEL_STATUS).optional(),
  /** Verweis auf das Protokoll, in dem der Mangel zuerst auftauchte. */
  herkunftProtokollId: z.string().optional(),
});
export type Mangel = z.infer<typeof mangelSchema>;

export const maengelblockWertSchema = z.array(mangelSchema);
export type MaengelblockWert = z.infer<typeof maengelblockWertSchema>;

export const schluesselZeileSchema = z.object({
  id: z.string().min(1),
  bezeichnung: z.string().min(1, 'Schlüsselart angeben').max(120),
  anzahl: z.number().int().min(0).max(999),
  nummer: z.string().max(120).optional(),
  uebergeben: z.number().int().min(0).max(999).optional(),
  bemerkung: z.string().max(500).optional(),
});
export type SchluesselZeile = z.infer<typeof schluesselZeileSchema>;

export const schluesseltabelleWertSchema = z.array(schluesselZeileSchema);
export type SchluesseltabelleWert = z.infer<typeof schluesseltabelleWertSchema>;

export const zaehlerstandWertSchema = z.object({
  art: z.string().min(1).max(60),
  nummer: z.string().max(120).optional(),
  stand: z.number().nullable(),
  einheit: z.string().max(16).optional(),
  abgelesenAm: z.string().optional(),
  fotos: z.array(fotoReferenzSchema).default([]),
});
export type ZaehlerstandWert = z.infer<typeof zaehlerstandWertSchema>;

export const beteiligterSchema = z.object({
  rolle: z.string().min(1).max(60),
  name: z.string().min(1).max(200),
  email: z.email('Keine gültige E-Mail-Adresse').optional().or(z.literal('')),
  telefon: z.string().max(60).optional(),
  firma: z.string().max(200).optional(),
  /** Empfänger des Protokoll-Links nach dem Versand. */
  erhaeltProtokoll: z.boolean().optional(),
});
export type Beteiligter = z.infer<typeof beteiligterSchema>;

export const beteiligteSchema = z.array(beteiligterSchema);

/** Signaturnachweis — bindet an den `dataHash`, nicht an das PDF. */
export const signaturSchema = z.object({
  id: z.string().min(1),
  rolle: z.string().min(1).max(60),
  name: z.string().min(1).max(200),
  /** SVG-Pfaddaten (`d`-Attribute), nicht das ganze SVG-Dokument. */
  pfadSvg: z.string().min(1, 'Es wurde nicht unterschrieben'),
  /** Zeichenfläche, auf die sich die Pfadkoordinaten beziehen. */
  breite: z.number().positive().default(600),
  hoehe: z.number().positive().default(200),
  signiertAm: z.string().min(1),
  dataHash: z.string().length(64, 'dataHash muss ein SHA-256-Hex-Wert sein'),
  geraeteId: z.string().optional(),
});
export type Signatur = z.infer<typeof signaturSchema>;

export const STANDARD_MANGEL_KATEGORIEN = [...MANGEL_KATEGORIEN_STANDARD];
export const STANDARD_ZAEHLER_ARTEN = [...ZAEHLER_ARTEN_STANDARD];

/**
 * Erkennung von Fotoreferenzen und Mängeln im Datenbaum.
 *
 * Bewusst über die genaue Schlüsselmenge statt über `schema.safeParse`: Zod
 * verwirft unbekannte Schlüssel stillschweigend, dadurch würde jede Zeile mit
 * einem `id`-Feld — also jede Raumzeile — als Foto durchgehen.
 */
const FOTO_SCHLUESSEL = new Set(['id', 'beschriftung', 'aufgenommenAm', 'breite', 'hoehe']);

export const istFotoReferenz = (wert: unknown): wert is FotoReferenz => {
  if (wert === null || typeof wert !== 'object' || Array.isArray(wert)) return false;
  const eintrag = wert as Record<string, unknown>;
  if (typeof eintrag.id !== 'string' || eintrag.id === '') return false;
  return Object.keys(eintrag).every((schluessel) => FOTO_SCHLUESSEL.has(schluessel));
};

export const istMangel = (wert: unknown): wert is Mangel => {
  if (wert === null || typeof wert !== 'object' || Array.isArray(wert)) return false;
  const eintrag = wert as Record<string, unknown>;
  return (
    typeof eintrag.id === 'string' &&
    typeof eintrag.beschreibung === 'string' &&
    Array.isArray(eintrag.fotos)
  );
};

/** Sammelt alle Fotoreferenzen aus einem beliebig verschachtelten Datenbaum. */
export const sammleFotoReferenzen = (
  wert: unknown,
  gesehen = new Set<string>(),
): FotoReferenz[] => {
  const ergebnis: FotoReferenz[] = [];
  const besuche = (knoten: unknown): void => {
    if (Array.isArray(knoten)) {
      for (const eintrag of knoten) besuche(eintrag);
      return;
    }
    if (knoten === null || typeof knoten !== 'object') return;
    if (istFotoReferenz(knoten)) {
      const referenz = fotoReferenzSchema.parse(knoten);
      if (!gesehen.has(referenz.id)) {
        gesehen.add(referenz.id);
        ergebnis.push(referenz);
      }
      return;
    }
    for (const eintrag of Object.values(knoten as Record<string, unknown>)) besuche(eintrag);
  };
  besuche(wert);
  return ergebnis;
};

/** Sammelt alle Mängel aus einem beliebig verschachtelten Datenbaum. */
export const sammleMaengel = (wert: unknown): Mangel[] => {
  const ergebnis: Mangel[] = [];
  const besuche = (knoten: unknown): void => {
    if (Array.isArray(knoten)) {
      for (const eintrag of knoten) besuche(eintrag);
      return;
    }
    if (knoten === null || typeof knoten !== 'object') return;
    if (istMangel(knoten)) {
      const kandidat = mangelSchema.safeParse(knoten);
      if (kandidat.success) {
        ergebnis.push(kandidat.data);
        return;
      }
    }
    for (const eintrag of Object.values(knoten as Record<string, unknown>)) besuche(eintrag);
  };
  besuche(wert);
  return ergebnis;
};
